import { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SIDEBAR_ASSOCIATIONS, mountainTodayISO, canonEvent, canonRodeoEvent, primaryAssoc, passesMoneyFilter } from "@/lib/ui";

const FiltersContext = createContext(null);

const readJSON = (k, fallback) => {
  try { const s = JSON.parse(localStorage.getItem(k)); return s == null ? fallback : s; }
  catch { return fallback; }
};
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

function initialAssoc() {
  const s = readJSON("rp_assoc_v4", null);
  // Nothing selected = show ALL. Picking associations narrows to ONLY those (same on every page).
  return new Set(Array.isArray(s) ? s : []);
}
function initialEvents() {
  const s = readJSON("rp_events", []);
  return new Set(Array.isArray(s) ? s : []);
}

const mondayISO = (d) => {
  const x = new Date(d); const wd = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

export function FiltersProvider({ children }) {
  // All filters are shared across every view AND persisted so they never reset.
  // Rodeos are fetched ONCE here and shared, so switching Calendar ⇄ Map doesn't refetch.
  // Rodeos are fetched ONCE (when the user is authenticated) and shared across every view.
  // FiltersProvider is mounted once at the app root, so navigating Table⇄Calendar⇄Map never refetches.
  const { user } = useAuth();
  const [rodeos, setRodeos] = useState(null);
  useEffect(() => {
    if (!user) { setRodeos(null); return; }
    let abort = false;
    api.get("/rodeos").then((r) => { if (!abort) setRodeos(r.data); }).catch(() => { if (!abort) setRodeos([]); });
    return () => { abort = true; };
  }, [user?.id]);
  // PRCA + CCA map rodeos are fetched ONCE here and shared, so re-opening the Map tab never refetches.
  const [prcaRodeos, setPrcaRodeos] = useState([]);
  const [ccaRodeos, setCcaRodeos] = useState([]);
  useEffect(() => {
    if (!user) { setPrcaRodeos([]); setCcaRodeos([]); return; }
    let abort = false, tries = 0;
    const loadPrca = () => api.get("/prca/map").then((r) => {
      if (abort) return;
      setPrcaRodeos(r.data.rodeos || []);
      if (r.data.pending > 0 && tries < 3) { tries += 1; setTimeout(loadPrca, 12000); }
    }).catch(() => {});
    loadPrca();
    api.get("/cca/map").then((r) => { if (!abort) setCcaRodeos(r.data.rodeos || []); }).catch(() => {});
    return () => { abort = true; };
  }, [user?.id]);
  // The Map pulls CCA/PRCA from dedicated feeds that use their own ids (e.g. "cca-044"),
  // while /rodeos stores the SAME rodeo under a Mongo id. Preferences are keyed by rodeo id,
  // so a pick on the Map wouldn't show on the Table. Normalize the feed ids to the canonical
  // /rodeos id (matched by shared performance id, then by name+city+start date) so every view agrees.
  const normStr = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const canonicalIndex = useMemo(() => {
    const byPerf = {}, byKey = {};
    (rodeos || []).forEach((r) => {
      (r.performances || []).forEach((p) => { if (p.id) byPerf[p.id] = r.id; });
      const sd = r.start_date || (r.performances || [])[0]?.date || "";
      byKey[`${normStr(r.name)}|${normStr(r.city)}|${String(sd).slice(0, 10)}`] = r.id;
    });
    return { byPerf, byKey };
  }, [rodeos]);
  const canonicalId = (r) => {
    const viaPerf = (r.performances || []).map((p) => canonicalIndex.byPerf[p.id]).find(Boolean);
    if (viaPerf) return viaPerf;
    const sd = r.start_date || (r.performances || [])[0]?.date || "";
    return canonicalIndex.byKey[`${normStr(r.name)}|${normStr(r.city)}|${String(sd).slice(0, 10)}`] || r.id;
  };
  const prcaRodeosN = useMemo(() => (prcaRodeos || []).map((r) => { const id = canonicalId(r); return id === r.id ? r : { ...r, id }; }), [prcaRodeos, canonicalIndex]);
  const ccaRodeosN = useMemo(() => (ccaRodeos || []).map((r) => { const id = canonicalId(r); return id === r.id ? r : { ...r, id }; }), [ccaRodeos, canonicalIndex]);

  const [assoc, setAssocState] = useState(initialAssoc);
  const [events, setEvents] = useState(initialEvents);
  const assocRef = useRef(assoc); assocRef.current = assoc;
  const eventsRef = useRef(events); eventsRef.current = events;
  const [circuits, setCircuits] = useState(() => new Set(readJSON("rp_circuits", [])));
  const [minMoney, setMinMoneyState] = useState(() => readJSON("rp_minmoney", 0));
  const [status, setStatusState] = useState(() => readJSON("rp_status", ""));
  const [country, setCountryState] = useState(() => readJSON("rp_country", ""));
  const [province, setProvinceState] = useState(() => readJSON("rp_province", ""));
  // Shared "selected rodeo" so tapping one on any view carries to the others.
  const [selectedRodeoId, setSelectedRodeoId] = useState(() => localStorage.getItem("rp_sel_rodeo") || null);
  const [focusDate, setFocusDate] = useState(null);
  // The rider's own disciplines (from their profile), used to DISPLAY per-event money
  // (e.g. "TD $5,000 · TR $5,000") on cards even when the event FILTER chips aren't toggled.
  const [profileEvents, setProfileEvents] = useState([]);
  useEffect(() => {
    if (!user?.id) { setProfileEvents([]); return; }
    let abort = false;
    api.get("/me/profile").then((r) => { if (!abort) setProfileEvents(Array.isArray(r.data?.events) ? r.data.events : []); }).catch(() => {});
    return () => { abort = true; };
  }, [user?.id]);
  // Shared, sticky date range across Calendar + Map (a Monday, 14-day window)
  const [weekStart, setWeekStart] = useState(() => localStorage.getItem("rp_weekstart") || mondayISO(new Date()));
  const [sortBy, setSortByState] = useState(() => localStorage.getItem("rp_sortby") || "default");
  const setSortBy = (v) => { localStorage.setItem("rp_sortby", v); setSortByState(v); };
  const [dateRange, setDateRangeState] = useState(() => {
    const saved = readJSON("rp_daterange", null);
    if (saved) return saved;
    const t = new Date();
    const isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { key: "this_month", from: isoD(new Date(t.getFullYear(), t.getMonth(), 1)), to: isoD(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [closingSoon, setClosingSoon] = useState(() => readJSON("rp_closing_soon", false));
  const toggleClosingSoon = () => setClosingSoon((v) => { const n = !v; write("rp_closing_soon", n); return n; });
  const persistWeek = (iso) => { setWeekStart(iso); localStorage.setItem("rp_weekstart", iso); };
  const shiftWeeks = (n) => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + n * 7); persistWeek(mondayISO(d)); };
  const weekToday = () => persistWeek(mondayISO(new Date()));

  const setDateRange = (key) => {
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const today = new Date();
    const plus = (n) => { const e = new Date(today); e.setDate(e.getDate() + n); return e; };
    let from = null, to = null;
    if (key === "this_week") { const m = new Date(weekStart + "T00:00:00"); from = iso(m); to = iso(new Date(m.getTime() + 6 * 864e5)); }
    else if (key === "next_week") { const m = new Date(weekStart + "T00:00:00"); m.setDate(m.getDate() + 7); from = iso(m); to = iso(new Date(m.getTime() + 6 * 864e5)); }
    else if (key === "this_month") { from = iso(new Date(today.getFullYear(), today.getMonth(), 1)); to = iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)); }
    else if (key === "upcoming") { from = iso(today); to = iso(plus(60)); }
    else if (key === "rest2026") { from = iso(today); to = "2026-12-31"; }
    else if (key === "season2027") { from = "2026-09-11"; to = "2027-09-30"; }
    const dr = { key, from, to };
    write("rp_daterange", dr); setDateRangeState(dr);
    if (from) persistWeek(mondayISO(new Date(from + "T00:00:00")));
  };

  const setCustomRange = (from, to) => {
    if (!from || !to) return;
    const dr = { key: "custom", from, to };
    write("rp_daterange", dr); setDateRangeState(dr);
    persistWeek(mondayISO(new Date(from + "T00:00:00")));
  };

  // Select a rodeo everywhere: remember it, move the shared date to its month,
  // and expose its date so the Calendar can jump to that day.
  const selectRodeo = (rodeo, opts = {}) => {
    if (!rodeo) return;
    setSelectedRodeoId(rodeo.id);
    try { localStorage.setItem("rp_sel_rodeo", rodeo.id); } catch { /* ignore */ }
    const d = (rodeo.performances || []).map((p) => p.date).filter(Boolean).sort()[0] || rodeo.start_date;
    if (d) {
      setFocusDate(d);
      try {
        localStorage.setItem("rp_m_cal_seldate", JSON.stringify(d));
        window.dispatchEvent(new CustomEvent("rp-persist", { detail: { key: "rp_m_cal_seldate" } }));
      } catch { /* ignore */ }
      if (opts.moveRange !== false) {
        const dt = new Date(d + "T00:00:00");
        const p2 = (n) => String(n).padStart(2, "0");
        const first = `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-01`;
        const last = `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-${p2(new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate())}`;
        setCustomRange(first, last);
      }
    }
  };

  const clearSelection = () => {
    setSelectedRodeoId(null);
    setFocusDate(null);
    try { localStorage.removeItem("rp_sel_rodeo"); } catch { /* ignore */ }
  };

  // Filters are PER USER. Reset to defaults whenever the logged-in account changes
  // (new signup or a different person on the same computer), so settings never transfer.
  const resetFilters = () => {
    setAssocState(new Set()); write("rp_assoc_v4", []);
    setEvents(new Set()); write("rp_events", []);
    setCircuits(new Set()); write("rp_circuits", []);
    setMinMoneyState(0); write("rp_minmoney", 0);
    setStatusState(""); write("rp_status", "");
    setCountryState(""); write("rp_country", "");
    setProvinceState(""); write("rp_province", "");
    setSortByState("default"); localStorage.setItem("rp_sortby", "default");
    setClosingSoon(false); write("rp_closing_soon", false);
    const t = new Date();
    const isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const dr = { key: "this_month", from: isoD(new Date(t.getFullYear(), t.getMonth(), 1)), to: isoD(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
    write("rp_daterange", dr); setDateRangeState(dr);
    persistWeek(mondayISO(new Date()));
  };
  useEffect(() => {
    const uid = user && user.id ? String(user.id) : (user && user.email) || null;
    if (!uid) return; // still checking / anonymous — don't wipe anything
    const owner = localStorage.getItem("rp_filters_owner");
    if (owner !== uid) { resetFilters(); localStorage.setItem("rp_filters_owner", uid); }
  }, [user?.id]); // eslint-disable-line

  // Associations + "Events I Enter" are ACCOUNT-level settings — they must follow the rider
  // across every device. On each login we hydrate them from the server profile (the source of
  // truth), so settings chosen on a phone show up on the desktop and vice-versa. If the account
  // has none saved yet but this device has local picks, we push them up (one-time migration).
  useEffect(() => {
    const uid = user && user.id ? String(user.id) : null;
    if (!uid) return;
    let abort = false;
    api.get("/me/profile").then((r) => {
      if (abort) return;
      const d = r.data || {};
      const a = Array.isArray(d.associations) ? d.associations : [];
      const e = Array.isArray(d.events) ? d.events : [];
      if (a.length || e.length) {
        setAssocList([...a], { persist: false });
        setEventsList([...new Set(e.map(canonEvent).filter(Boolean))], { persist: false });
      } else {
        const localA = [...assocRef.current];
        const localE = [...eventsRef.current];
        if (localA.length) api.post("/me/associations", { associations: localA }).catch(() => {});
        if (localE.length) api.post("/me/events", { events: localE }).catch(() => {});
      }
    }).catch(() => {});
    return () => { abort = true; };
  }, [user?.id]); // eslint-disable-line

  const persistAssoc = (arr) => { if (user?.id) api.post("/me/associations", { associations: arr }).catch(() => {}); };
  const persistEvents = (arr) => { if (user?.id) api.post("/me/events", { events: arr }).catch(() => {}); };

  const toggleAssoc = (a) => setAssocState((prev) => {
    const n = new Set(prev); n.has(a) ? n.delete(a) : n.add(a);
    const arr = [...n]; write("rp_assoc_v4", arr); persistAssoc(arr); return n;
  });
  const setAssocList = (arr, opts = {}) => { write("rp_assoc_v4", arr); setAssocState(new Set(arr)); if (opts.persist !== false) persistAssoc(arr); };
  const toggleEvent = (e) => setEvents((prev) => {
    const n = new Set(prev); n.has(e) ? n.delete(e) : n.add(e);
    const arr = [...n]; write("rp_events", arr); persistEvents(arr); return n;
  });
  const setEventsList = (arr, opts = {}) => { write("rp_events", arr); setEvents(new Set(arr)); if (opts.persist !== false) persistEvents(arr); };

  // Bridge the rider's saved PROFILE preferences (associations + events, from onboarding/
  // My Season) into the ACTIVE list filters, so "set the scene once" actually sticks across
  // Table/Calendar/Map. Onboarding labels (e.g. "Bareback", "Ladies Breakaway Roping") are
  // canonicalised to match the data's event names.
  const applyProfilePrefs = ({ associations, events: evs } = {}) => {
    if (Array.isArray(associations)) setAssocList([...associations]);
    if (Array.isArray(evs)) setEventsList([...new Set(evs.map(canonEvent).filter(Boolean))]);
  };
  const toggleCircuit = (c) => setCircuits((prev) => {
    const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c);
    write("rp_circuits", [...n]); return n;
  });
  const clearCircuits = () => { write("rp_circuits", []); setCircuits(new Set()); };
  const setMinMoney = (v) => { write("rp_minmoney", v); setMinMoneyState(v); };
  const setStatus = (v) => { write("rp_status", v); setStatusState(v); };
  const setProvince = (v) => { write("rp_province", v); setProvinceState(v); };
  // changing country resets province
  const selectCountry = (c) => { write("rp_country", c); write("rp_province", ""); setCountryState(c); setProvinceState(""); };

  const _todayISO = mountainTodayISO();
  const _soonISO = new Date(Date.parse(_todayISO + "T00:00:00Z") + 7 * 864e5).toISOString().slice(0, 10);
  // Event filter: when the rider has events selected, hide rodeos whose offered-events
  // list excludes ALL of them. Rodeos with no known events are kept (we can't judge them).
  const eventOk = (r) => {
    if (events.size === 0) return true;
    const evs = r.events || [];
    if (!evs.length) return true;
    const rset = new Set([...events].map(canonRodeoEvent));
    return evs.some((e) => rset.has(canonRodeoEvent(e)));
  };
  const matches = (r) =>
    (assoc.size === 0 || (r.associations || []).some((a) => assoc.has(a))) &&
    (circuits.size === 0 || circuits.has(r.circuit_name)) &&
    (minMoney <= 0 || passesMoneyFilter(r, displayEvents, minMoney)) &&
    eventOk(r) &&
    (!status || r.status === status) &&
    (!country || (r.country || "Canada") === country) &&
    (!province || (r.province || "Unspecified") === province) &&
    (closingSoon
      ? (r.entry_close && r.entry_close >= _todayISO && r.entry_close <= _soonISO)
      : (!dateRange.from || (r.performances || []).some((p) => p.date && p.date >= dateRange.from && p.date <= dateRange.to)));

  const activeFilterCount =
    (country ? 1 : 0) + (status ? 1 : 0) + (minMoney > 0 ? 1 : 0) +
    (events.size > 0 ? 1 : 0) + (assoc.size > 0 ? 1 : 0) + (circuits.size > 0 ? 1 : 0) + (closingSoon ? 1 : 0);

  // Events to use for DISPLAYING per-discipline money on cards: the active event filter if
  // the rider set one, otherwise their profile disciplines. This keeps "TD $X · TR $Y"
  // showing for a TD/TR rider even when they clear the event chips to browse everything.
  const displayEvents = useMemo(
    () => (events.size ? events : new Set((profileEvents || []).map(canonEvent).filter(Boolean))),
    [events, profileEvents]
  );

  return (
    <FiltersContext.Provider value={{
      assoc, toggleAssoc, setAssocList, events, toggleEvent, setEventsList, applyProfilePrefs, displayEvents,
      circuits, toggleCircuit, clearCircuits,
      minMoney, setMinMoney, status, setStatus,
      country, setCountry: selectCountry, province, setProvince, matches,
      weekStart, shiftWeeks, weekToday,
      sortBy, setSortBy,
      dateRange, setDateRange, setCustomRange,
      rodeos, prcaRodeos: prcaRodeosN, ccaRodeos: ccaRodeosN,
      selectedRodeoId, selectRodeo, clearSelection, focusDate,
      filtersOpen, setFiltersOpen, activeFilterCount,
      closingSoon, toggleClosingSoon,
    }}>
      {children}
    </FiltersContext.Provider>
  );
}

export const useFilters = () => useContext(FiltersContext);
